import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import H4 from 'UIComponents/elements/headings/H4';
import UIFlex from 'UIComponents/layout/UIFlex';
import { EditPlaylistButton, useFullPlaylist } from '..';
import { OverviewSource, useAllTracks } from '../../tracks';
import styled, { theme } from '../../../utils/Styling';
import { FormattedMessage } from '../../../components/I18n';
import UICopyButton from 'UIComponents/button/UICopyButton';
import StartPlaylistButton from './StartPlaylistButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import H7 from 'UIComponents/elements/headings/H7';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS } from '../../../constants/Tracking';
import { useUserOwnsPlaylist } from '../../savedItems';
import { UserAttributeKeys, useUserAttribute } from '../../userAttributes';
const PlaylistBreakdownList = styled(UIList).withConfig({
  displayName: "PlaylistDetails__PlaylistBreakdownList",
  componentId: "sc-1uiicd3-0"
})(["&& > li{padding-left:0;}"]);
const PlaylistBreakdownListItem = styled.div.withConfig({
  displayName: "PlaylistDetails__PlaylistBreakdownListItem",
  componentId: "sc-1uiicd3-1"
})(["display:flex;align-items:center;margin-right:12px;&& > span{margin-right:8px;}"]);
const IncludesHeading = styled(H7).withConfig({
  displayName: "PlaylistDetails__IncludesHeading",
  componentId: "sc-1uiicd3-2"
})(["margin-bottom:8px;"]);
const PlaylistActions = styled.section.withConfig({
  displayName: "PlaylistDetails__PlaylistActions",
  componentId: "sc-1uiicd3-3"
})(["display:flex;gap:12px;margin-bottom:16px;&& button{margin:0;}"]);
const CertificationsIcon = styled((_ref) => {
  let props = Object.assign({}, _ref);
  return /*#__PURE__*/_jsx(UIIcon, Object.assign({
    color: theme.colors.EERIE,
    name: "certification",
    size: "xs"
  }, props));
}).withConfig({
  displayName: "PlaylistDetails__CertificationsIcon",
  componentId: "sc-1uiicd3-4"
})(["position:relative;top:4px;"]);

const usePlaylistBreakdown = playlistItems => {
  const {
    allTracks
  } = useAllTracks();
  const breakdown = {
    certifications: 0,
    lessons: 0
  };

  for (const playlistItem of playlistItems) {
    if (playlistItem.type === 'LESSON') {
      breakdown.lessons++;
    } else if (playlistItem.type === 'TRACK') {
      const track = allTracks[playlistItem.itemId];
      if (track && track.lessonIds) breakdown.lessons += track.lessonIds.length;

      if (track && track.certification) {
        breakdown.certifications++;
      }
    }
  }

  return breakdown;
};

const PlaylistBreakdown = ({
  playlistItems
}) => {
  const breakdown = usePlaylistBreakdown(playlistItems);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(IncludesHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.savedItems.playlistDetails.includes"
      })
    }), /*#__PURE__*/_jsxs(PlaylistBreakdownList, {
      inline: true,
      children: [/*#__PURE__*/_jsxs(PlaylistBreakdownListItem, {
        children: [/*#__PURE__*/_jsx(CertificationsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfCertifications",
          options: {
            count: breakdown.certifications
          }
        })]
      }), /*#__PURE__*/_jsxs(PlaylistBreakdownListItem, {
        children: [/*#__PURE__*/_jsx(UIIcon, {
          color: theme.colors.EERIE,
          name: "presentation",
          size: "xs"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfLessons",
          options: {
            count: breakdown.lessons
          }
        })]
      })]
    })]
  });
};

export const PlaylistDetails = ({
  playlistId
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const {
    setUserAttribute
  } = useUserAttribute(UserAttributeKeys.HAS_VIEWED_PLAYLIST);
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: SCREENS.PLAYLIST
    });
  }, []);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setUserAttribute(true);
  }, [setUserAttribute]);
  if (!playlist) return null;

  const trackPlaylistShare = () => {
    UsageTracker.track('playlistInteraction', {
      action: 'share-playlist',
      isOwner: userIsOwner,
      playlistId,
      location: 'playlist-details'
    });
  };

  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsx(H4, {
        children: playlist.name
      }), /*#__PURE__*/_jsx(EditPlaylistButton, {
        $isHiddenFor: "mobile",
        playlistId: playlistId
      })]
    }), /*#__PURE__*/_jsxs(PlaylistActions, {
      children: [/*#__PURE__*/_jsx(StartPlaylistButton, {
        appearsIn: OverviewSource.Playlist,
        block: false,
        playlistId: playlistId,
        showLoader: true,
        style: {
          flex: 1,
          maxWidth: 260
        }
      }), /*#__PURE__*/_jsx(UICopyButton, {
        hintSuccess: "Link copied",
        onClick: trackPlaylistShare,
        tooltipPlacement: "bottom",
        use: "tertiary",
        value: window.location.href,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.common.share"
        })
      })]
    }), /*#__PURE__*/_jsx("p", {
      children: playlist.description
    }), /*#__PURE__*/_jsx(PlaylistBreakdown, {
      playlistItems: playlist.items
    })]
  });
};